import { cn } from "@/lib/utils";
import type { Options as JsxRuntimeOptions } from "hast-util-to-jsx-runtime";
import type { ComponentProps } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";

// Helper function to filter out internal ReactMarkdown/Hast props
export const filterProps = (props: Record<string, unknown>) => {
  const { node, ...filteredProps } = props;
  return filteredProps;
};

// Default JSX runtime factory for static content (JSON, etc)
export const createStaticJsxRuntime = (): JsxRuntimeOptions => {
  return {
    jsx,
    jsxs,
    components: {
      span: ({
        children,
        style: _style,
        className = "",
        ...props
      }: ComponentProps<"span">) => (
        <span className={className} style={_style} {...filterProps(props)}>
          {children}
        </span>
      ),
      pre: ({
        children,
        className,
        style: _style,
        ...props
      }: ComponentProps<"pre">) => (
        <pre
          {...filterProps(props)}
          className={cn(
            className,
            "text-xs overflow-x-auto whitespace-pre-wrap font-mono",
          )}
        >
          {children}
        </pre>
      ),
      code: ({
        className,
        children,
        style: _style,
        ...props
      }: ComponentProps<"code">) => (
        <code className={className} {...filterProps(props)}>
          {children}
        </code>
      ),
    },
    Fragment,
    development: false,
  };
};

// Streaming JSX runtime factory (for animated content like SourceCode)
export const createStreamingJsxRuntime = (
  durationRef?: React.MutableRefObject<string>,
): JsxRuntimeOptions => {
  const duration = durationRef?.current ?? "duration-300";

  return {
    jsx,
    jsxs,
    components: {
      span: ({
        children,
        style: _style,
        className = "",
        ...props
      }: ComponentProps<"span">) => {
        const isLine = className.includes("line");

        if (isLine) {
          const lineNumber =
            props &&
            "data-line" in props &&
            typeof props["data-line"] === "number"
              ? props["data-line"]
              : null;

          return (
            <span
              key={lineNumber ?? undefined}
              className={cn(className, "animate-fadeIn", duration)}
              {...filterProps(props)}
            >
              {children}
            </span>
          );
        }

        return (
          <span
            className={cn(className, "animate-fadeIn", duration)}
            style={_style}
            {...filterProps(props)}
          >
            {children}
          </span>
        );
      },
      pre: ({
        children,
        className,
        style: _style,
        ...props
      }: ComponentProps<"pre">) => (
        <pre
          {...filterProps(props)}
          className={cn(className, "animate-fadeIn", duration)}
        >
          {children}
        </pre>
      ),
      code: ({
        inline,
        className,
        children,
        style: _style,
        ...props
      }: ComponentProps<"code"> & { inline?: boolean }) => {
        return inline ? (
          <code
            className={cn(className, "animate-fadeIn", duration)}
            {...filterProps(props)}
          >
            {children}
          </code>
        ) : (
          <code
            key="block"
            className={cn(className, "animate-fadeIn", duration)}
            {...filterProps(props)}
          >
            {children}
          </code>
        );
      },
    },
    Fragment,
    development: false,
  };
};
